import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconLink } from '@iwoca/icons';
import { Trans } from '@lingui/react/macro';

import { BankSelectionModal } from './BankSelectionModal/BankSelectionModal';
import { TOnlineBank } from './OpenBanking.types';
import { inferProvider } from './trueLayer.helpers';
import styles from './TrueLayer.module.css';
import { useGetContext } from '../../../api/lendingApi/lendingApiQueries';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useIsInDEOpenBankingCopyTest } from '../../../hooks/useIsInDEOpenBankingCopyTest';
import { useLocale } from '../../../hooks/useLocale';
import { useSignupParams } from '../../../hooks/useSignupParams';
import { useSplitTestContext } from '../../../hooks/useSplitTestContext';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import { Button } from '../../Button/Button';

type TProps = {
  disabled?: boolean;
};

const LinkOpenBanking = ({ disabled }: TProps) => {
  const stateKey = useContextStateKey();
  const { isDE } = useLocale();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { context } = useGetContext();
  const { isDirectCustomer } = useSignupParams();
  const { hasOpenBankingShareableLinkEnabled } = useSplitTestContext();

  const { isIncludedInOBCopyTest } = useIsInDEOpenBankingCopyTest();

  const getBankList = (): TOnlineBank[] => {
    if (!context) return [];

    const deOrderedBanks =
      context.open_banking.de_ordered_open_banking_providers;
    if (deOrderedBanks && deOrderedBanks.length > 0) {
      return deOrderedBanks.map((bank) => ({
        ...bank,
        provider: inferProvider(bank.bank_id),
      }));
    }

    const truelayerBanks: TOnlineBank[] = (
      context.open_banking.truelayer_banks ?? []
    ).map((bank) => ({ ...bank, provider: 'truelayer' as const }));

    const finapiBanks: TOnlineBank[] = (
      context.open_banking.finapi_banks ?? []
    ).map((bank) => ({ ...bank, provider: 'finapi' as const }));

    return [...truelayerBanks, ...finapiBanks];
  };

  const handleClick = () => {
    if (!stateKey) return;

    tracking.ampli.openBankingConnectStarted({
      ...getAmplitudeDefaultEventProperties(),
      is_in_open_banking_copy_test: isIncludedInOBCopyTest,
    });

    return setIsModalOpen(true);
  };

  const handleBankSelectionClose = () => {
    tracking.ampli.trueLayerModalClosed();
    setIsModalOpen(false);
  };

  const buttonText = isIncludedInOBCopyTest ? (
    <Trans>Share bank statements</Trans>
  ) : (
    <Trans>Link bank account</Trans>
  );
  const ableToShareLink =
    isDirectCustomer && hasOpenBankingShareableLinkEnabled;

  return (
    <>
      <Button
        className={styles.button}
        variant="secondary"
        onClick={handleClick}
        disabled={disabled || !stateKey}
      >
        {buttonText}
        <IconLink />
      </Button>

      <BankSelectionModal
        isOpen={isModalOpen}
        handleClose={handleBankSelectionClose}
        bankList={getBankList()}
        shouldSkipPermissionView={isDE}
        stateKey={stateKey || ''}
        ableToShareLink={ableToShareLink}
      />
    </>
  );
};

export default LinkOpenBanking;
